import { valuesAreEqual } from './common';

import type { EnhancedFormFieldValues } from '../types/record-form';
import type { FormFieldValue } from '../types/forms';

interface ReconcileRecordValuesParams {
  previousValues: EnhancedFormFieldValues;
  serverValues: EnhancedFormFieldValues;
  debouncedFieldValues: Record<string, FormFieldValue>;
  fieldsBeingSaved: Set<string>;
  preserveRecordValues: boolean;
}

export const reconcileRecordValues = ({
  previousValues,
  serverValues,
  debouncedFieldValues,
  fieldsBeingSaved,
  preserveRecordValues,
}: ReconcileRecordValuesParams): {
  values: EnhancedFormFieldValues;
  remoteChangedFields: string[];
} => {
  if (preserveRecordValues && Object.keys(previousValues).length > 0) {
    return { values: previousValues, remoteChangedFields: [] };
  }

  const values = { ...serverValues };
  const preservedFields = new Set<string>();
  const changedFields: string[] = [];
  const isInitialLoad = Object.keys(previousValues).length === 0;

  if (!isInitialLoad) {
    Object.keys(serverValues).forEach((fieldName) => {
      if (!valuesAreEqual(previousValues[fieldName], serverValues[fieldName])) {
        changedFields.push(fieldName);
      }
    });
  }

  Object.keys(debouncedFieldValues).forEach((fieldName) => {
    if (previousValues[fieldName] !== undefined) {
      values[fieldName] = previousValues[fieldName];
      preservedFields.add(fieldName);
    }
  });

  fieldsBeingSaved.forEach((fieldName) => {
    values[fieldName] = previousValues[fieldName];
    preservedFields.add(fieldName);
  });

  return {
    values,
    remoteChangedFields: changedFields.filter((field) => !preservedFields.has(field)),
  };
};
